/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import ContainerInventoryTable from '../partials/containerTable';
import { getContainerUnitColumns } from '../partials/containerUnitColumns';
import { ContainerInventoryService } from '../../../../../service/MediaContainerServices';
import ContainerDetailsHeader from './containerDetailsHeader';
import ContainerDetailsFooter from './containerDetailsFooter';
import ContainerUnitDetails from './containerUnitDetails';

interface LotInfo {
  id: string;
  lot_code: string;
  lot_serial: string;
  lot_size: string;
  created_at: string;
}

interface ContainerLotDetailsProps {
  lot: LotInfo;
  batchId: string;
  onBack: () => void;
  onViewUnit?: (unitId: string) => void;
}

const ContainerLotDetails: React.FC<ContainerLotDetailsProps> = ({ lot, batchId, onBack, onViewUnit }) => {
    const [selectedLotId, setSelectedLotId] = React.useState<string | null>(null);
    const [containerList, setContainerList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
    const { t } = useTranslation();


  const fetchContainerDetails = async () => {
    try {
      setLoading(true);
      const res =
        await ContainerInventoryService.getContainerLotContainerDetails(lot.id, batchId);

      if (res?.status) {
        setContainerList(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching lot container details", error);
    } finally {
      setLoading(false);
    }
  };      

  useEffect(() => {
    if (lot?.id && batchId) {
      fetchContainerDetails();
    }
  }, [lot.id, batchId]);


  if (selectedUnitId) {
    return (
      <ContainerUnitDetails
        unitId={selectedUnitId}
        onBack={async () => {
          setSelectedUnitId(null);
          await fetchContainerDetails(); 
        }}
      />
    );
  }     


  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-20">
       <MediaPrepHeaderSection />
         <MediaPrepPageHeader pageHeader={t('containerInventory.containerLotDetails.pageTitle')}  />
          {/* Sticky Header */}
        <ContainerDetailsHeader
          pageTitle={t("containerInventory.containerLotDetails.pageTitle")}
          identifier={lot.lot_code}
          printLabel={t("containerInventory.containerLotDetails.printUnitLabel")}
          onBack={onBack}
        />

        {/* Premium Informative Statistics Strip */}
        <div className="sticky top-[224px] z-30 bg-[#FBFDFE] border-b border-slate-100 py-3 px-10">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex-1 flex flex-col gap-1 border-r border-slate-100 pr-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.view_list')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerLotDetails.LotName')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50] truncate">{lot.lot_serial}</span>
            </div>

            <div className="flex-1 flex flex-col gap-1 border-r border-slate-100 px-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.inventory_2')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerLotDetails.TotalQuantity')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50]">{lot.lot_size} Units</span>
            </div>

            <div className="flex-1 flex flex-col gap-1 pl-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.calendar_today')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerLotDetails.CreatedDate')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50]">{lot.created_at?.split(' ')[0] || "-"}</span>
            </div>
          </div>
      </div>
      

      <div className="p-12">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-300">
            <div className="px-8 py-6 flex justify-between items-center border-b border-slate-50 bg-[#FBFDFE]">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-orange-500 font-bold">{t('containerInventory.icons.view_cozy')}</span>
                  <h3 className="text-base font-black text-[#2D3E50] tracking-tight uppercase">{t('containerInventory.containerLotDetails.ContainerWiseDetails')}:   {lot.lot_code}</h3>
                </div>
            </div>

            <ContainerInventoryTable
                columns={getContainerUnitColumns(t)}
                data={containerList}
                actionHeader="Actions"
                renderAction={(row) => (
                    <button
                    onClick={() => setSelectedUnitId(row.container_id)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-black text-[9px] uppercase tracking-widest hover:border-blue-300 hover:text-blue-600 transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-95 ml-auto group"
                    >
                    <span className="material-symbols-outlined text-2xl">
                        {t("containerInventory.icons.visibility")}
                    </span>
                    {t("containerInventory.containerBatchDetails.ViewDetails")}
                    </button>
                )}
            />
          </div>

        {/* Footer Actions */}
        <ContainerDetailsFooter onBack={onBack} />
      </div>
    </div>
  );
};

export default ContainerLotDetails;
